// src/components/ClubForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { normalizeClub, formatClubForAPI } from '../utils/fieldCompatibility';
import './ClubForm.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

// Country and Province data
const COUNTRIES = [
  'South Africa',
  'Botswana',
  'Namibia',
  'Zimbabwe',
  'Mozambique',
  'Lesotho',
  'Eswatini',
  'Zambia',
  'Malawi',
  'Angola',
  'Tanzania',
  'Democratic Republic of Congo',
  'Republic of Congo',
  'Madagascar',
  'Mauritius',
  'Seychelles',
  'Comoros',
  'Liberia',
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Poland',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Bulgaria',
  'Greece',
  'Portugal',
  'Ireland',
  'New Zealand',
  'Japan',
  'China',
  'India',
  'Brazil',
  'Argentina',
  'Mexico',
  'Chile',
  'Peru',
  'Colombia',
  'Venezuela',
  'Ecuador',
  'Uruguay',
  'Paraguay',
  'Bolivia',
  'Cuba',
  'Jamaica',
  'Trinidad and Tobago',
  'Barbados',
  'Bahamas',
  'Other'
];

const PROVINCES_BY_COUNTRY = {
  'South Africa': [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape'
  ],
  'Botswana': [
    'Central District',
    'Ghanzi District',
    'Kgalagadi District',
    'Kgatleng District',
    'Kweneng District',
    'North-East District',
    'North-West District',
    'South-East District',
    'Southern District'
  ],
  'Namibia': [
    'Erongo Region',
    'Hardap Region',
    'Kavango East Region',
    'Kavango West Region',
    'Khomas Region',
    'Kunene Region',
    'Ohangwena Region',
    'Omaheke Region',
    'Omusati Region',
    'Oshana Region',
    'Oshikoto Region',
    'Otjozondjupa Region',
    'Zambezi Region'
  ],
  'Zimbabwe': [
    'Bulawayo Province',
    'Harare Province',
    'Manicaland Province',
    'Mashonaland Central Province',
    'Mashonaland East Province',
    'Mashonaland West Province',
    'Masvingo Province',
    'Matabeleland North Province',
    'Matabeleland South Province',
    'Midlands Province'
  ],
  'Mozambique': [
    'Cabo Delgado Province',
    'Gaza Province',
    'Inhambane Province',
    'Manica Province',
    'Maputo City',
    'Maputo Province',
    'Nampula Province',
    'Niassa Province',
    'Sofala Province',
    'Tete Province',
    'Zambezia Province'
  ],
  'Lesotho': [
    'Berea District',
    'Butha-Buthe District',
    'Leribe District',
    'Mafeteng District',
    'Maseru District',
    'Mohales Hoek District',
    'Mokhotlong District',
    'Qachas Nek District',
    'Quthing District',
    'Thaba-Tseka District'
  ],
  'Eswatini': [
    'Hhohho Region',
    'Lubombo Region',
    'Manzini Region',
    'Shiselweni Region'
  ],
  'Zambia': [
    'Central Province',
    'Copperbelt Province',
    'Eastern Province',
    'Luapula Province',
    'Lusaka Province',
    'Muchinga Province',
    'Northern Province',
    'North-Western Province',
    'Southern Province',
    'Western Province'
  ],
  'Malawi': [
    'Central Region',
    'Northern Region',
    'Southern Region'
  ],
  'Angola': [
    'Bengo Province',
    'Benguela Province',
    'Bié Province',
    'Cabinda Province',
    'Cuando Cubango Province',
    'Cuanza Norte Province',
    'Cuanza Sul Province',
    'Cunene Province',
    'Huambo Province',
    'Huíla Province',
    'Luanda Province',
    'Lunda Norte Province',
    'Lunda Sul Province',
    'Malanje Province',
    'Moxico Province',
    'Namibe Province',
    'Uíge Province',
    'Zaire Province'
  ],
  'Liberia': [
    'Bomi County',
    'Bong County',
    'Gbarpolu County',
    'Grand Bassa County',
    'Grand Cape Mount County',
    'Grand Gedeh County',
    'Grand Kru County',
    'Lofa County',
    'Margibi County',
    'Maryland County',
    'Montserrado County',
    'Nimba County',
    'River Cess County',
    'River Gee County',
    'Sinoe County'
  ],
  'United States': [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming'
  ],
  'Canada': [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon'
  ],
  'Australia': [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia'
  ],
  'United Kingdom': [
    'England',
    'Scotland',
    'Wales',
    'Northern Ireland'
  ],
  'Germany': [
    'Baden-Württemberg',
    'Bavaria',
    'Berlin',
    'Brandenburg',
    'Bremen',
    'Hamburg',
    'Hesse',
    'Lower Saxony',
    'Mecklenburg-Vorpommern',
    'North Rhine-Westphalia',
    'Rhineland-Palatinate',
    'Saarland',
    'Saxony',
    'Saxony-Anhalt',
    'Schleswig-Holstein',
    'Thuringia'
  ],
  'France': [
    'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté',
    'Brittany',
    'Centre-Val de Loire',
    'Corsica',
    'Grand Est',
    'Hauts-de-France',
    'Île-de-France',
    'Normandy',
    'Nouvelle-Aquitaine',
    'Occitanie',
    'Pays de la Loire',
    'Provence-Alpes-Côte d\'Azur'
  ],
  'Italy': [
    'Abruzzo',
    'Aosta Valley',
    'Apulia',
    'Basilicata',
    'Calabria',
    'Campania',
    'Emilia-Romagna',
    'Friuli-Venezia Giulia',
    'Lazio',
    'Liguria',
    'Lombardy',
    'Marche',
    'Molise',
    'Piedmont',
    'Sardinia',
    'Sicily',
    'Trentino-Alto Adige',
    'Tuscany',
    'Umbria',
    'Veneto'
  ],
  'Spain': [
    'Andalusia',
    'Aragon',
    'Asturias',
    'Balearic Islands',
    'Basque Country',
    'Canary Islands',
    'Cantabria',
    'Castile and León',
    'Castile-La Mancha',
    'Catalonia',
    'Extremadura',
    'Galicia',
    'La Rioja',
    'Madrid',
    'Navarre',
    'Valencian Community'
  ],
  'Other': ['Other']
};

const ClubForm = ({ existingClub, onClubSubmitted, onCancel }) => {
  const { selectedCompetition } = useCompetition();
  const [clubName, setClubName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [country, setCountry] = useState('South Africa'); // Default to South Africa
  const [province, setProvince] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitializingRef = useRef(false);

  // Initialize form with existing data if editing
  useEffect(() => {
    if (existingClub) {
      // Set initializing flag to prevent province reset during country change
      isInitializingRef.current = true;

      // Normalize the club data for consistent field access
      const normalizedClub = normalizeClub(existingClub);
      setClubName(normalizedClub.name || '');
      setContactPerson(normalizedClub.contactPerson || '');
      setContactNumber(normalizedClub.contactNumber || '');
      setCountry(normalizedClub.country || 'South Africa');
      setProvince(normalizedClub.province || '');

      // Clear initializing flag after state updates
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
    } else {
      // Reset to default values for new club
      isInitializingRef.current = false;
      setClubName('');
      setContactPerson('');
      setContactNumber('');
      setCountry('South Africa');
      setProvince('');
    }
  }, [existingClub]);

  // Reset province when country changes (but not during form initialization)
  useEffect(() => {
    // Only reset province if we're not initializing from an existing club
    if (!isInitializingRef.current && !existingClub) {
      setProvince('');
    }
  }, [country, existingClub]);

  // Validate form inputs
  const validateForm = () => {
    if (!clubName.trim()) {
      setError('Club/Studio name is required');
      return false;
    }

    if (!contactPerson.trim()) {
      setError('Contact person is required');
      return false;
    }

    if (!contactNumber.trim()) {
      setError('Contact number is required');
      return false;
    }

    // Basic phone number validation (can be enhanced)
    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (!phoneRegex.test(contactNumber.trim())) {
      setError('Please enter a valid contact number');
      return false;
    }

    if (!country.trim()) {
      setError('Country is required');
      return false;
    }

    if (!province.trim()) {
      setError('Province/State is required');
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const clubData = {
        name: clubName.trim(),
        contactPerson: contactPerson.trim(),
        contactNumber: contactNumber.trim(),
        country: country.trim(),
        province: province.trim(),
      };

      // Add competition ID if available
      if (selectedCompetition && selectedCompetition.id) {
        clubData.competitionId = selectedCompetition.id;
      }

      let url;
      let method;

      if (existingClub) {
        // Update existing club
        url = `https://${_host}:6003/api/${_company}/clubs/${existingClub.id}`;
        method = 'PUT';
        clubData.id = existingClub.id;
      } else {
        // Create new club
        url = `https://${_host}:6003/api/${_company}/clubs`;
        method = 'POST';
      }

      // Format club data for API submission with both field naming conventions
      const formattedClubData = formatClubForAPI(clubData);
      console.log('🔍 Submitting club data:', {
        original: clubData,
        formatted: formattedClubData
      });

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedClubData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save club');
      }

      const savedClub = await response.json();

      // Call the callback with the saved club data
      onClubSubmitted(savedClub);

    } catch (err) {
      console.error('Error saving club:', err);
      setError(err.message || 'Failed to save club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (setter) => {
    return (e) => {
      setter(e.target.value);
      if (error) {
        setError(null);
      }
    };
  };

  return (
    <div className="club-form-container">
      <div className="club-form-header">
        <h3>{existingClub ? 'Edit Club/Studio' : 'Add New Club/Studio'}</h3>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="club-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clubName" className="form-label">
              Club/Studio Name *
            </label>
            <input
              type="text"
              id="clubName"
              value={clubName}
              onChange={handleInputChange(setClubName)}
              className="form-input"
              placeholder="Enter club or studio name"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="contactPerson" className="form-label">
              Contact Person *
            </label>
            <input
              type="text"
              id="contactPerson"
              value={contactPerson}
              onChange={handleInputChange(setContactPerson)}
              className="form-input"
              placeholder="Enter contact person's name"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="contactNumber" className="form-label">
              Contact Number *
            </label>
            <input
              type="tel"
              id="contactNumber"
              value={contactNumber}
              onChange={handleInputChange(setContactNumber)}
              className="form-input"
              placeholder="Enter contact number (e.g., +27 12 345 6789)"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country *
            </label>
            <select
              id="country"
              value={country}
              onChange={handleInputChange(setCountry)}
              className="form-select"
              disabled={loading}
              required
            >
              {COUNTRIES.map((countryOption) => (
                <option key={countryOption} value={countryOption}>
                  {countryOption}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="province" className="form-label">
              Province/State *
            </label>
            <select
              id="province"
              value={province}
              onChange={handleInputChange(setProvince)}
              className="form-select"
              disabled={loading}
              required
            >
              <option value="">Select Province/State</option>
              {PROVINCES_BY_COUNTRY[country]?.map((provinceOption) => (
                <option key={provinceOption} value={provinceOption}>
                  {provinceOption}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="cancel-btn"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Saving...' : (existingClub ? 'Update Club' : 'Add Club')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClubForm;